import type { Address, Hex } from "viem";
import { z } from "zod/mini";
import { checksum, hex } from "../schemas/primitives";
import type { GenericStorage } from "../types";
import type { EncryptedValue } from "../relayer/relayer-sdk.types";

const STORAGE_PREFIX = "zama:pending-unshield:";
const CURRENT_VERSION = 1;

/**
 * Persisted state for an in-progress unshield request.
 * Used to resume an interrupted unshield after page reload.
 *
 * @internal
 */
export interface PendingUnshieldRequest {
  /** Transaction hash of the original unwrap call. */
  readonly unwrapTxHash: Hex;
  /**
   * Request identifier from the `UnwrapRequested` event.
   * Always populated for entries persisted by this SDK version; pass it as
   * `unwrapRequestId` to `finalizeUnwrap`. Absent only when re-loading a
   * pending unshield serialized by an older SDK version that did not
   * record this field — in that case pass the `encryptedAmount` from the
   * `UnwrapRequested` event to `finalizeUnwrap` instead.
   */
  readonly unwrapRequestId?: EncryptedValue;
}

interface StoredPendingUnshieldRequest extends PendingUnshieldRequest {
  readonly version: typeof CURRENT_VERSION;
}

function storageKey(wrapperAddress: Address): string {
  return `${STORAGE_PREFIX}${checksum(wrapperAddress)}`;
}

const PendingUnshieldRequestSchema = z.union([
  z.pipe(
    hex,
    z.transform((unwrapTxHash: Hex) => ({ unwrapTxHash })),
  ),
  z.pipe(
    z.object({
      version: z.literal(CURRENT_VERSION),
      unwrapTxHash: hex,
      unwrapRequestId: z.optional(hex),
    }),
    z.transform(
      ({
        unwrapTxHash,
        unwrapRequestId,
      }: {
        version: typeof CURRENT_VERSION;
        unwrapTxHash: Hex;
        unwrapRequestId?: Hex;
      }) => ({ unwrapTxHash, unwrapRequestId }),
    ),
  ),
]);

/**
 * Persist the unwrap tx hash so an interrupted unshield can be resumed later
 * (e.g. after a page reload).
 *
 * @internal
 */
export async function savePendingUnshield(
  storage: GenericStorage,
  wrapperAddress: Address,
  unwrapTxHash: Hex,
  unwrapRequestId?: EncryptedValue,
): Promise<void> {
  if (unwrapRequestId === undefined) {
    await storage.set(storageKey(wrapperAddress), unwrapTxHash);
    return;
  }

  await storage.set(storageKey(wrapperAddress), {
    version: CURRENT_VERSION,
    unwrapTxHash,
    unwrapRequestId,
  } satisfies StoredPendingUnshieldRequest);
}

/**
 * Load a previously saved unwrap tx hash, or `null` if none exists.
 *
 * @internal
 */
export async function loadPendingUnshield(
  storage: GenericStorage,
  wrapperAddress: Address,
): Promise<Hex | null> {
  const request = await loadPendingUnshieldRequest(storage, wrapperAddress);
  return request?.unwrapTxHash ?? null;
}

/**
 * Load a previously saved unwrap request, including `unwrapRequestId` when available.
 *
 * `resumeUnshield()` only needs `unwrapTxHash`: it reloads the transaction receipt and
 * rediscovers the right finalize input from the emitted `UnwrapRequested` event.
 * Use `unwrapRequestId` directly only for custom flows that call `finalizeUnwrap()`
 * without reloading the original unwrap receipt.
 *
 * @internal
 */
export async function loadPendingUnshieldRequest(
  storage: GenericStorage,
  wrapperAddress: Address,
): Promise<PendingUnshieldRequest | null> {
  const key = storageKey(wrapperAddress);
  const value = await storage.get<Hex | StoredPendingUnshieldRequest>(key);
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = PendingUnshieldRequestSchema.safeParse(value);
  if (!parsed.success) {
    await storage.delete(key);
    return null;
  }
  return parsed.data as PendingUnshieldRequest;
}

/**
 * Clear the saved unwrap tx hash after a successful finalization.
 *
 * @internal
 */
export async function clearPendingUnshield(
  storage: GenericStorage,
  wrapperAddress: Address,
): Promise<void> {
  await storage.delete(storageKey(wrapperAddress));
}
